import { useEffect, useState } from "react";
import { Megaphone, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { day } from "@/lib/money";
import FilePick from "@/components/FilePick";
import Attachment from "@/components/community/Attachment";

function PostBox({ api, onPosted }) {
  const [text, setText] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const post = async () => {
    if (text.trim().length < 3 && !file) return toast.error("Write a little more first.");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("message", text.trim());
      if (file) fd.append("file", file);
      const { data } = await api.post("/admin/announcements", fd);
      onPosted(data);
      setText("");
      setFile(null);
      toast.success("Announcement posted for all clients.");
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not post. Try again.");
    } finally {
      setBusy(false);
    }
    return null;
  };
  return (
    <div className="mb-3">
      <textarea value={text} onChange={(e) => setText(e.target.value)} maxLength={1000} rows={3} data-testid="announcement-input"
        placeholder="Share an update with every client — e.g. what you're building next based on their suggestions."
        className="w-full px-3 py-2.5 bg-[#141414] border border-[#262626] text-white text-base sm:text-sm resize-y focus:outline-none focus:border-white" />
      <div className="mt-2 flex flex-wrap items-center gap-3">
        <FilePick file={file} onFile={setFile} testid="announcement-attach" />
        <button onClick={post} disabled={busy || (!text.trim() && !file)} data-testid="announcement-post"
          className="min-h-[40px] px-4 cta-primary font-mono-label text-[11px] inline-flex items-center gap-2 disabled:opacity-50">
          {busy ? <Loader2 size={13} className="animate-spin" /> : <Megaphone size={13} />} Post to all clients
        </button>
      </div>
    </div>
  );
}

export default function Announcements({ api, isAdmin }) {
  const [items, setItems] = useState(null);
  useEffect(() => {
    api.get("/announcements").then(({ data }) => setItems(data.announcements)).catch(() => setItems([]));
  }, [api]);
  if (!items || (!isAdmin && items.length === 0)) return null;
  const remove = async (id) => {
    if (!window.confirm("Delete this announcement for everyone?")) return;
    try {
      await api.delete(`/admin/announcements/${id}`);
      setItems((x) => x.filter((a) => a.id !== id));
    } catch {
      toast.error("Could not delete. Try again.");
    }
  };
  return (
    <section className="brutal-border border-[#FFD600]/50 bg-[#FFD600]/[0.04] p-4 sm:p-5 mb-6" data-testid="announcements">
      <div className="font-mono-label text-[10px] text-[#FFD600] inline-flex items-center gap-1.5 mb-3"><Megaphone size={12} /> UPDATES FROM THE CARRIERLAUNCH TEAM</div>
      {isAdmin && <PostBox api={api} onPosted={(a) => setItems((x) => [a, ...x])} />}
      {items.length === 0 && <p className="text-sm text-muted-foreground" data-testid="announcements-empty">No announcements yet. Clients see these at the top of App Suggestions.</p>}
      <div className="divide-y divide-[#262626]">
        {items.map((a) => (
          <div key={a.id} className="py-2.5 flex gap-3" data-testid={`announcement-${a.id}`}>
            <div className="flex-1 min-w-0">
              <div className="text-[11px] text-muted-foreground">{day(a.created_at)}</div>
              {a.message && <p className="text-sm mt-0.5 whitespace-pre-wrap break-words">{a.message}</p>}
              {a.attachment && <Attachment api={api} url={`/announcements/${a.id}/file`} att={a.attachment} testid={`announcement-att-${a.id}`} />}
            </div>
            {isAdmin && (
              <button onClick={() => remove(a.id)} aria-label="Delete announcement" data-testid={`announcement-delete-${a.id}`}
                className="w-9 h-9 shrink-0 flex items-center justify-center text-muted-foreground hover:text-[#F87171]"><Trash2 size={14} /></button>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}
